/**
 * /admin/v1/snapshots/* API 客户端 · 三 module 版本管理（S2 Day 8b）
 *
 * 承接：apps/api/src/csa_api/admin/snapshot_admin.py（Day 8a · 9 endpoint · PR #27）
 * 设计：generic hooks 接受 module 参数（l2_rules / intent_mapping / playbook/{pb_id}）
 *      · 路径在 buildPath 集中拼，避免每个调用点拼字符串
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/lib/api-client";

// ─── DTO ─────────────────────────────────────────────────────────────────

export interface SnapshotMeta {
	snapshot_id: string;
	timestamp: string;
	author: string;
	message: string;
	file_basename: string;
	size_bytes: number;
}

export interface SnapshotListResponse {
	items: SnapshotMeta[];
	total: number;
}

export interface SnapshotSaveRequest {
	author: string;
	message: string;
}

export interface SnapshotSaveResponse {
	snapshot: SnapshotMeta;
	bytes_written: number;
}

export interface SnapshotRestoreResponse {
	snapshot_id: string;
	restored_at: string;
	bytes_written: number;
	cache_invalidated: boolean;
}

export type SnapshotModule =
	| { kind: "l2_rules" }
	| { kind: "intent_mapping" }
	| { kind: "playbook"; pb_id: string };

function buildBase(module: SnapshotModule): string {
	if (module.kind === "playbook") {
		return `/admin/v1/snapshots/playbook/${encodeURIComponent(module.pb_id)}`;
	}
	return `/admin/v1/snapshots/${module.kind}`;
}

function moduleQueryKey(module: SnapshotModule): readonly unknown[] {
	if (module.kind === "playbook") {
		return ["admin", "snapshots", "playbook", module.pb_id];
	}
	return ["admin", "snapshots", module.kind];
}

// ─── hooks ───────────────────────────────────────────────────────────────

export function useListSnapshots(module: SnapshotModule, enabled = true) {
	return useQuery<SnapshotListResponse>({
		queryKey: [...moduleQueryKey(module), "list"],
		queryFn: async () => {
			const { data } = await apiClient.get<SnapshotListResponse>(`${buildBase(module)}/list`);
			return data;
		},
		enabled,
	});
}

export function useSaveSnapshot(module: SnapshotModule) {
	const qc = useQueryClient();
	return useMutation<SnapshotSaveResponse, unknown, SnapshotSaveRequest>({
		mutationFn: async (body) => {
			const { data } = await apiClient.post<SnapshotSaveResponse>(
				`${buildBase(module)}/save`,
				body,
			);
			return data;
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: moduleQueryKey(module) });
		},
	});
}

export function useRestoreSnapshot(module: SnapshotModule) {
	const qc = useQueryClient();
	return useMutation<SnapshotRestoreResponse, unknown, { snapshot_id: string }>({
		mutationFn: async ({ snapshot_id }) => {
			const { data } = await apiClient.post<SnapshotRestoreResponse>(
				`${buildBase(module)}/restore/${encodeURIComponent(snapshot_id)}`,
				{},
			);
			return data;
		},
		onSuccess: () => {
			// restore 后需要让对应 module 的 list / yaml 全部 refetch
			qc.invalidateQueries({ queryKey: moduleQueryKey(module) });
			if (module.kind === "l2_rules") {
				qc.invalidateQueries({ queryKey: ["admin", "intent"] });
			} else if (module.kind === "intent_mapping" || module.kind === "playbook") {
				qc.invalidateQueries({ queryKey: ["admin", "playbook"] });
			}
		},
	});
}
